// Browser entrypoint. Bundled to public/beacon.js by esbuild.beacon.mjs.
//
// Two modes (set via data-page attribute or window.CAPI_CONFIG.page):
//   "pageview"     — every funnel page. Fires PageView, identifies the
//                    visitor, populates platform custom fields, captures
//                    user-typed PII into the store.
//   "confirmation" — the post-conversion page. Also fires Lead/Purchase with
//                    a canonical event_id and notifies /pending-events.

import { readConfig, type BeaconConfig } from './config';
import { bootPixel, reinitWithAdvancedMatching, trackEvent } from './pixel';
import {
  collectPii, getFbc, getFbp, getVisitorId,
  restoreMetaCookies, captureMetaSignals,
} from './identity';
import { hashAllPresent, hashAM, type AMInputs } from '../src/lib/hash';
import { buildEventId } from '../src/lib/event_id';
import { extractClickFunnels } from './extractors/clickfunnels';
import { extractStealthSeminar } from './extractors/stealthseminar';
import { setCookieDomain, resolveRootDomain } from './cookie';
import {
  loadStore, getStore, updateStore,
} from './store';
import {
  detectPlatform, customFieldMapFor, fillCustomFields,
  watchDefaults, scheduleAntiGarlicRefills, watchCFSubmitTrigger, flushDefaults,
} from './autofill';

async function postJson(url: string, body: unknown): Promise<Response | null> {
  try {
    return await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    });
  } catch (err) {
    console.warn('[capi-beacon] post failed', url, err);
    return null;
  }
}

// Build hashed manual-AM from the store. The store holds Meta-normalized
// values, so each hashAM call is effectively a SHA-256 of the stored string.
async function buildHashesFromStore(visitorId: string): Promise<AMInputs> {
  const store = getStore();
  const out: AMInputs = {};
  if (store.email)      out.em      = await hashAM('em',      store.email);
  if (store.phone)      out.ph      = await hashAM('ph',      store.phone);
  if (store.first_name) out.fn      = await hashAM('fn',      store.first_name);
  if (store.last_name)  out.ln      = await hashAM('ln',      store.last_name);
  if (store.city)       out.ct      = await hashAM('ct',      store.city);
  if (store.state)      out.st      = await hashAM('st',      store.state);
  if (store.zip)        out.zp      = await hashAM('zp',      store.zip);
  if (store.country)    out.country = await hashAM('country', store.country);
  out.external_id = await hashAM('external_id', visitorId);
  return out;
}

async function reinitFromStore(cfg: BeaconConfig, visitorId: string): Promise<AMInputs> {
  const hashes = await buildHashesFromStore(visitorId);
  reinitWithAdvancedMatching(cfg.pixel_id, hashes);
  return hashes;
}

async function postIdentify(cfg: BeaconConfig, visitorId: string, hashes: AMInputs): Promise<void> {
  const store = getStore();
  const res = await postJson(`${cfg.api_base}/identify`, {
    visitor_id: visitorId,
    // Use canonical (first-touch) fbp/fbc from the store, not whatever the
    // current host cookie holds. This is what carries ad-click attribution
    // through the entire funnel — opt-in page → registration → purchase.
    fbp: store._fbp ?? getFbp(),
    fbc: store._fbc ?? getFbc(),
    user_agent: navigator.userAgent,
    url: window.location.href,
    hashes,
  });
  // Server tells us the request IP — stash it so custom fields can carry it.
  if (!res) return;
  try {
    const data = await res.clone().json() as { ip?: string };
    if (data.ip) updateStore({ ip: data.ip });
  } catch { /* ignore non-JSON responses */ }
}

async function bootIdentity(cfg: BeaconConfig): Promise<{ visitorId: string; hashes: AMInputs }> {
  // Resolve the cookie domain BEFORE any cookie writes. Use override if
  // provided; otherwise probe for the broadest registrable root the
  // browser will accept (handles .com and public-suffix TLDs alike).
  if (typeof cfg.cookie_domain === 'string') {
    setCookieDomain(cfg.cookie_domain || null);
  } else {
    setCookieDomain(resolveRootDomain());
  }

  loadStore();

  // Restore canonical _fbp/_fbc to host cookies BEFORE the pixel snippet
  // runs so fbevents.js reads first-touch values. The pixel is initialized
  // by bootPixel below; the install function only stages the loader, the
  // remote script loads asynchronously and reads cookies at that time.
  restoreMetaCookies();

  bootPixel(cfg.pixel_id);

  const visitorId = await getVisitorId();

  // Volatile per-page signals (always overwrite — these reflect the
  // current page, not first-touch).
  updateStore({
    uuid: visitorId,
    page_url: window.location.href,
    user_agent: navigator.userAgent,
  });

  // First-touch-sticky: _fbp once set never overwrites; _fbc follows the
  // same rule unless a fresh fbclid is on the URL (a new ad click).
  captureMetaSignals();

  // Pre-existing PII on the page (from autofill, dataLayer, etc.)
  const pii = collectPii();
  if (pii.em || pii.ph || pii.fn || pii.ln) {
    const { sanitizePii } = await import('./sanitize');
    const sanitized = sanitizePii({
      email: pii.em,
      phone: pii.ph,
      first_name: pii.fn,
      last_name: pii.ln,
    });
    if (Object.keys(sanitized).length) updateStore(sanitized);
  }

  const platform = detectPlatform();
  let customMap: ReturnType<typeof customFieldMapFor> | null = null;
  if (platform) {
    customMap = customFieldMapFor(platform);
    // Use the multi-pass refill schedule for *both* platforms. CF runs
    // garlic.js, and SS is a SPA whose React app can re-render forms after
    // we fill them. Same defense fits both.
    //
    // captureMetaSignals runs before each refill pass — fbevents.js sets
    // the _fbp cookie asynchronously after our initial boot, so this is
    // how that fresh value gets pulled into the store and propagated into
    // the custom fields the vendor will eventually webhook back to us.
    scheduleAntiGarlicRefills(customMap, captureMetaSignals);
    // Capture any pre-filled defaults (e.g., browser autofill on email).
    flushDefaults(platform);
  }

  // First identity beacon (best-available hashes pre-IP).
  let hashes = await reinitFromStore(cfg, visitorId);
  await postIdentify(cfg, visitorId, hashes);

  // Re-fill custom fields now that ip has landed in the store.
  if (customMap) fillCustomFields(customMap, getStore());

  // Watch user input — re-hash, re-init pixel, re-identify, refresh customs.
  if (platform) {
    watchDefaults(platform, () => {
      // Fire-and-forget; debounce isn't worth the complexity here.
      void (async () => {
        const fresh = await reinitFromStore(cfg, visitorId);
        if (customMap) fillCustomFields(customMap, getStore());
        await postIdentify(cfg, visitorId, fresh);
      })();
    });

    if (platform === 'clickfunnels' && customMap) {
      const map = customMap;
      watchCFSubmitTrigger(map, () => {
        void (async () => {
          const fresh = await reinitFromStore(cfg, visitorId);
          fillCustomFields(map, getStore());
          await postIdentify(cfg, visitorId, fresh);
        })();
      });
    }
  } else {
    // No SS/CF context — fall back to a generic input listener so we still
    // catch email/phone changes on plain pages.
    attachGenericFormListeners(cfg, visitorId, hashes);
  }

  return { visitorId, hashes };
}

function attachGenericFormListeners(cfg: BeaconConfig, visitorId: string, lastHashes: AMInputs): void {
  let current = lastHashes;
  const handler = async (ev: Event) => {
    const target = ev.target as HTMLInputElement | null;
    if (!target || !('value' in target)) return;
    const fresh = await hashAllPresent(collectPii());
    const merged: AMInputs = { ...current, ...fresh };
    let changed = false;
    for (const k of Object.keys(merged) as (keyof AMInputs)[]) {
      if (merged[k] !== current[k]) { changed = true; break; }
    }
    if (!changed) return;
    current = merged;
    reinitWithAdvancedMatching(cfg.pixel_id, merged);
    await postJson(`${cfg.api_base}/identify`, { visitor_id: visitorId, hashes: merged });
  };
  document.addEventListener('change', handler, true);
  document.addEventListener('blur', handler, true);
}

async function fireConversion(cfg: BeaconConfig, visitorId: string): Promise<void> {
  if (!cfg.source || !cfg.event_name) {
    console.warn('[capi-beacon] confirmation page missing source/event_name config');
    return;
  }

  let extracted: { vendor_event_id: string; value?: number; currency?: string; order_id?: string } | null = null;
  if (cfg.source === 'clickfunnels')        extracted = extractClickFunnels();
  else if (cfg.source === 'stealthseminar') extracted = extractStealthSeminar();

  if (!extracted || !extracted.vendor_event_id) {
    console.warn('[capi-beacon] could not extract vendor_event_id; skipping conversion fire');
    return;
  }

  const eventId = buildEventId(cfg.event_name, visitorId, extracted.vendor_event_id);

  // Per-page-load idempotency. Belt-and-suspenders alongside Meta's own
  // event_id dedup — saves a wasted pixel hit if two confirmation tags
  // both target the same conversion.
  const fired = (W[FIRED_KEY] as Set<string> | undefined) ?? new Set<string>();
  if (fired.has(eventId)) {
    console.log('[capi-beacon] conversion already fired this page load:', eventId);
    return;
  }
  fired.add(eventId);
  W[FIRED_KEY] = fired;

  const customData: Record<string, unknown> = {};
  const value = extracted.value ?? cfg.fallback_value;
  const currency = extracted.currency ?? cfg.fallback_currency;
  if (value !== undefined) customData.value = value;
  if (currency)            customData.currency = currency;
  if (extracted.order_id)  customData.order_id = extracted.order_id;

  trackEvent(cfg.event_name, customData, eventId);

  const store = getStore();
  await postJson(`${cfg.api_base}/pending-events`, {
    event_id: eventId,
    event_name: cfg.event_name,
    source: cfg.source,
    visitor_id: visitorId,
    vendor_event_id: extracted.vendor_event_id,
    email_hash: store.email ? await hashAM('em', store.email) : null,
    phone_hash: store.phone ? await hashAM('ph', store.phone) : null,
    fired_at: Date.now(),
  });
}

// Multiple beacon <script> tags can land on the same page — most commonly when
// a platform forces a global pageview tag and adds a per-page confirmation
// tag on top (StealthSeminar's "global" + "additional" pattern, or CF's
// funnel-wide + per-step tags). The first instance to run does the identity
// boot; later instances only fire their own conversion. Conversion fires are
// keyed by event_id, so even if both somehow ran them, Meta would dedupe.
const W = window as unknown as Record<string, unknown>;
const BOOT_KEY = '__capi_beacon_booted';
const FIRED_KEY = '__capi_beacon_fired';

async function main(): Promise<void> {
  let cfg: BeaconConfig;
  try { cfg = readConfig(); }
  catch (err) { console.error(err); return; }

  // Synchronous flag check + set, before any await, so two script tags
  // executing back-to-back can't both pass this gate.
  const alreadyBooted = !!W[BOOT_KEY];

  let visitorId: string;
  if (alreadyBooted) {
    visitorId = await getVisitorId();
  } else {
    W[BOOT_KEY] = true;
    const result = await bootIdentity(cfg);
    visitorId = result.visitorId;
  }

  if (cfg.page === 'confirmation') {
    await fireConversion(cfg, visitorId);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { void main(); });
} else {
  void main();
}
